import React, { useEffect, useMemo, useRef } from 'react'

import { Formik } from 'formik'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import ReactTooltip from 'react-tooltip'

import { AlertFlag } from '../AlertFlag'
import CustomNumberFormat from '@/components/CustomFields/components/CustomNumberFormat'
import DSCRCalculatorField from '@/components/DSCRCalculator'
import { NumberField, SelectField } from '@/components/FormikInputs'
import AssetManagementOverridesForm from '@/features/DealSummary/AssetManagementOverrides/Form'
import { Application } from '@/graphql/generated'
import { useGetApplicationOfferCalcsQuery } from '@/graphql/services/applicationCalcs'
import {
  useGetOverrideByApplicationQuery,
  useUpdateOverrideByApplicationMutation,
} from '@/services/override'
import { useGetLoggedUserQuery } from '@/services/user'
import { Overrides } from '@/types/unlock'
import { shallowObjectCompare } from '@/utils'
import { FEATURES_LIST } from '@/utils/features/featuresList'
import { useFeatureFlag } from '@/utils/features/splitSdk'
import useShouldHideDTI from '@/utils/useShouldHideDTI'
import { dscrEditAllowed, isUAMUser, isUWUser, isUnderwritingUser } from '@/utils/user'

// New deals use a different system to adjust the exchange rate
export const useERAdjustment = (app: Application) => {
  if (!app) return false
  return app.pricingVersion >= 5.0
}

export const useERAdjusmentOptions = () => {
  return useMemo(() => {
    const options = [
      { label: '-0.50X', value: '-0.50' },
      { label: '-0.45X', value: '-0.45' },
      { label: '-0.40X', value: '-0.40' },
      { label: '-0.35X', value: '-0.35' },
      { label: '-0.30X', value: '-0.30' },
      { label: '-0.25X', value: '-0.25' },
      { label: '-0.20X', value: '-0.20' },
      { label: '-0.15X', value: '-0.15' },
      { label: '-0.10X', value: '-0.10' },
      { label: '-0.05X', value: '-0.05' },
      { label: '0.00X', value: '0.00' },
      { label: '+0.05X', value: '0.05' },
      { label: '+0.10X', value: '0.10' },
      { label: '+0.15X', value: '0.15' },
      { label: '+0.20X', value: '0.20' },
      { label: '+0.25X', value: '0.25' },
      { label: '+0.30X', value: '0.30' },
      { label: '+0.35X', value: '0.35' },
      { label: '+0.40X', value: '0.40' },
      { label: '+0.45X', value: '0.45' },
      { label: '+0.50X', value: '0.50' },
    ]
    return options
  }, [])
}

function AssetManagementOverridesSection(props: { application: Application }) {
  const router = useRouter()

  const { component } = router.query

  const assets = useRef(null)

  const { application } = props

  const { data: loggedUser } = useGetLoggedUserQuery()

  const { data: overrides } = useGetOverrideByApplicationQuery(application?.uuid, {
    skip: !application,
  })

  const { data: applicationOfferResponse } = useGetApplicationOfferCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application,
    }
  )

  const shouldHideDTI = useShouldHideDTI()

  const applicationOffer = useMemo(
    () => applicationOfferResponse?.application?.offerCalculations,
    [applicationOfferResponse]
  )

  const isOverridesAlowed =
    isUAMUser(loggedUser) ||
    isUWUser(loggedUser) ||
    isUnderwritingUser(loggedUser) ||
    loggedUser?.is_superuser

  const useERA = useERAdjustment(application)

  const [updateOverrides] = useUpdateOverrideByApplicationMutation()
  const isInvestmentProperty = application?.property?.propertyUsage === 'investment'

  const rateFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 })

  const initialValues = {
    total_home_financing_limit:
      overrides?.total_home_financing_limit !== null
        ? overrides?.total_home_financing_limit * 100
        : null,
    annualized_cost_limit:
      overrides?.annualized_cost_limit !== null
        ? parseFloat((overrides?.annualized_cost_limit * 100).toFixed(5))
        : null,
    cltv_limit: overrides?.cltv_limit !== null ? overrides?.cltv_limit * 100 : null,
    exchange_rate: overrides?.exchange_rate,
    exchange_rate_max_offer: overrides?.exchange_rate_max_offer,
    adjusted_exchange_rate: overrides?.adjusted_exchange_rate,
    exchange_rate_adjustment: rateFormat.format(Number(overrides?.exchange_rate_adjustment || 0)),
    term: overrides?.term,
    min_investment: overrides?.min_investment,
    dti: overrides?.dti !== null ? (overrides?.dti * 100).toFixed(1) : null,
    max_investment: overrides?.max_investment,
    home_value: overrides?.home_value,
    fico: overrides?.fico,
    exception_approved: overrides?.exception_approved ?? false,
    uuid: overrides?.uuid,
    dscr: overrides?.dscr,
    pricing_version: application?.pricingVersion,
  }

  const handleCreditScoreChanges = (values: Partial<Overrides>) => {
    // Max investment needs to be updated if credit score changes for pricing version 21
    if (values.fico !== initialValues.fico && application.pricingVersion >= 21) {
      const creditScore = values.fico ?? application.applicationFico
      values.max_investment = creditScore < 550 ? 250_000 : 500_000
    }
  }

  const handleSubmit = async (values: Partial<Overrides>, { setSubmitting }) => {
    handleCreditScoreChanges(values)

    if (!shallowObjectCompare(initialValues, values)) {
      try {
        toast.dismiss({ containerId: 'feedbackLoop' })
        await updateOverrides({
          applicationUUID: application?.uuid,
          overrides: {
            ...values,
            annualized_cost_limit:
              values.annualized_cost_limit !== null
                ? parseFloat((values.annualized_cost_limit / 100).toFixed(5))
                : null,
            total_home_financing_limit:
              values.total_home_financing_limit !== null
                ? values.total_home_financing_limit / 100
                : null,
            cltv_limit: values.cltv_limit !== null ? values.cltv_limit / 100 : null,
            dti: values.dti !== null ? (values.dti / 100).toFixed(3) : null,
          },
        }).unwrap()
        toast.success('Updated', { containerId: 'feedbackLoop' })
      } catch (error) {
        toast.error('Error: Changes not saved', { containerId: 'feedbackLoop' })
      } finally {
        setSubmitting(false)
      }
    }
  }

  useEffect(() => {
    if (router.isReady && (component == 'assets' || router.asPath.includes('#assets'))) {
      setTimeout(() => assets.current.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500)
      window.scrollBy(0, window.screen.width <= 1440 ? -350 : 0)
    }
  }, [router])

  const baseExchangeRate = applicationOffer?.selectedOffer?.offers?.baseExchangeRate

  const adjustmentOptions = useERAdjusmentOptions()

  const ERLimit = useMemo(() => {
    if ([6.0, 7.0].includes(application.pricingVersion)) return Number(baseExchangeRate) + 0.1
    if ([8.0, 9.0, 10.0, 11.0].includes(application.pricingVersion)) return 2.4
    if ([2.7, 12.0, 13.0, 14.0, 15.0, 16.0, 17.0].includes(application.pricingVersion)) return 2.2
    if ([18.0, 19.0, 20.0, 21.0].includes(application.pricingVersion)) return 2.05
    return 2.0
  }, [application, overrides?.exchange_rate_adjustment, baseExchangeRate])

  const exceedLimit = useMemo(() => {
    const adjustedExchangeRate =
      Number(baseExchangeRate) + Number(overrides?.exchange_rate_adjustment)

    return Number(adjustedExchangeRate.toFixed(2)) > ERLimit
  }, [ERLimit, overrides?.exchange_rate_adjustment, baseExchangeRate])

  const showLimitTooltip = useMemo(() => {
    return (
      (Number(baseExchangeRate) + Number(overrides?.exchange_rate_adjustment) > 2 &&
        application.pricingVersion == 5.0) ||
      application.pricingVersion >= 6.0
    )
  }, [baseExchangeRate, overrides?.exchange_rate_adjustment, exceedLimit])

  const disableLegacyOnBlurToSave = useFeatureFlag(FEATURES_LIST.DISABLE_LEGACY_ONBLUR_TO_SAVE)

  return (
    <>
      {disableLegacyOnBlurToSave ? (
        <AssetManagementOverridesForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          useERA={useERA}
          overrides={overrides}
          adjustmentOptions={adjustmentOptions}
          allowOverrides={isOverridesAlowed}
          baseExchangeRate={baseExchangeRate}
          showLimitTooltip={showLimitTooltip}
          ERLimit={ERLimit}
          exceedLimit={exceedLimit}
          applicationOffer={applicationOffer}
          rateFormat={rateFormat}
          shouldHideDTI={shouldHideDTI}
          isInvestmentProperty={isInvestmentProperty}
          application={application}
          dscrEditAllowed={dscrEditAllowed}
          loggedUser={loggedUser}
        />
      ) : (
        <section
          className='card border shadow bg-base-100 h-full'
          id='assets'
          data-test='asset-management-overrides-section'
          ref={assets}
        >
          <div className='card-body'>
            <h4 className='card-title'>Offer Overrides</h4>

            <div className='flex gap-12 mt-8'>
              <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleSubmit}
              >
                {({ values, submitForm, isSubmitting }) => (
                  <table className='table table-sm table-auto'>
                    <tbody>
                      <tr>
                        <td>Total Home Financing (THF)</td>
                        <td>
                          <NumberField
                            name='total_home_financing_limit'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            suffix='%'
                            prefix=''
                            decimalScale={2}
                            thousandSeparator={false}
                          />
                        </td>
                        <td>Annualized Cost Limit (ACL)</td>
                        <td>
                          <NumberField
                            name='annualized_cost_limit'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            suffix='%'
                            prefix=''
                            thousandSeparator={false}
                            decimalScale={2}
                          />
                        </td>
                      </tr>
                      <tr className='border-dashed border-t border-key-grey-l4'>
                        <td>Combined Loan to Value (CLTV)</td>
                        <td className='w-32'>
                          <NumberField
                            name='cltv_limit'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            suffix='%'
                            prefix=''
                            decimalScale={2}
                            thousandSeparator={false}
                          />
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>
                          {useERA ? 'Exchange Rate Adjustment' : 'Exchange Rate (Current)'}
                          {overrides?.exchange_rate_adjustment_type && (
                            <span className='ml-2 badge badge-sm bg-emerald-400 rounded-3xl font-normal'>
                              {overrides?.exchange_rate_adjustment_type.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td>
                          {useERA ? (
                            <SelectField
                              name='exchange_rate_adjustment'
                              readOnly={isSubmitting || !isOverridesAlowed}
                              options={adjustmentOptions}
                              onChange={submitForm}
                              hasPlaceholder={false}
                            />
                          ) : (
                            <NumberField
                              name='exchange_rate'
                              onBlur={submitForm}
                              readOnly={isSubmitting || !isOverridesAlowed}
                              decimalScale={2}
                              thousandSeparator={false}
                              prefix=''
                              suffix='X'
                            />
                          )}
                        </td>
                        <td>Term</td>
                        <td>
                          <NumberField
                            name='term'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            thousandSeparator={false}
                            prefix=''
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className='text-gray-500 py-4'>
                          {useERA ? 'Exchange Rate' : 'Exchange Rate (Max)'}
                        </td>
                        <td>
                          {useERA ? (
                            <>
                              <ReactTooltip
                                id={'ER'}
                                place={'right'}
                              />
                              <p
                                data-tip={`Base ER: ${baseExchangeRate ? baseExchangeRate : '-'}${
                                  showLimitTooltip ? ` - (ER limit is ${ERLimit}X)` : ''
                                }`}
                                data-for='ER'
                                className={`text-[16px] font-semibold px-1 ${
                                  exceedLimit ? 'text-error' : ''
                                }`}
                              >
                                {baseExchangeRate
                                  ? `${rateFormat.format(
                                      applicationOffer?.selectedOffer?.offers?.exchangeRate
                                    )}X`
                                  : '~'}
                              </p>
                            </>
                          ) : (
                            <NumberField
                              name='exchange_rate_max_offer'
                              onBlur={submitForm}
                              readOnly={isSubmitting || !isOverridesAlowed}
                              decimalScale={2}
                              thousandSeparator={false}
                              prefix=''
                              suffix='X'
                            />
                          )}
                        </td>
                        <td className='flex'>
                          DSCR {<AlertFlag flag='missing-data-dscr-on-rental-property' />}
                        </td>
                        <td>
                          {isInvestmentProperty ? (
                            <DSCRCalculatorField
                              applicationUUUID={application.uuid}
                              applicationId={application.id}
                              overridesValue={values.dscr}
                              onBlur={submitForm}
                              readOnly={isSubmitting || !dscrEditAllowed(loggedUser)}
                            />
                          ) : (
                            <NumberField
                              name='dscr'
                              onBlur={submitForm}
                              readOnly={isSubmitting || !isOverridesAlowed}
                              decimalScale={2}
                              thousandSeparator={false}
                              prefix=''
                              suffix='X'
                            />
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Min Investment</td>
                        <td>
                          <CustomNumberFormat
                            name='min_investment'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            field
                            roundingSystem='down'
                          />
                        </td>
                        <td>Max Investment</td>
                        <td>
                          <CustomNumberFormat
                            name='max_investment'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            field
                            roundingSystem='down'
                          />
                        </td>
                      </tr>

                      <tr>
                        <td>Credit Score</td>
                        <td>
                          <NumberField
                            name='fico'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            prefix=''
                            decimalScale={2}
                          />
                        </td>
                        <td>Pricing Version</td>
                        <td>
                          <NumberField
                            name='pricing_version'
                            value={application?.pricingVersion}
                            onBlur={submitForm}
                            readOnly
                            prefix=''
                            decimalScale={2}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Home Value</td>
                        <td>
                          <CustomNumberFormat
                            name='home_value'
                            onBlur={submitForm}
                            readOnly={isSubmitting || !isOverridesAlowed}
                            decimalScale={0}
                            field
                            roundingSystem='closer'
                          />
                        </td>
                        {!shouldHideDTI && (
                          <>
                            <td>DTI</td>
                            <td>
                              <NumberField
                                name='dti'
                                onBlur={submitForm}
                                readOnly={isSubmitting || !isOverridesAlowed}
                                suffix='%'
                                prefix=''
                                thousandSeparator={false}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    </tbody>
                  </table>
                )}
              </Formik>
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default AssetManagementOverridesSection
